import React from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, Users, BookOpen, Award, User, Download, Menu, ChevronDown, Search, UserPlus } from 'lucide-react';
import AthleteBooklet from '../AthleteBooklet';
import StarRating from '../StarRating';
import CompetitionAnalysis from '../CompetitionAnalysis';
import { toast } from 'sonner';
import { useDashboard } from './DashboardContext';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaestroSelector = ({ user }) => {
  const [maestros, setMaestros] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [currentMaestro, setCurrentMaestro] = React.useState(null);
  const [conflictData, setConflictData] = React.useState(null);
  const [pendingMaestroId, setPendingMaestroId] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/available-maestros`);
        setMaestros(res.data);
        if (user.maestro_id) {
          const m = res.data.find(m => m.id === user.maestro_id);
          setCurrentMaestro(m || null);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [user.maestro_id]);

  const selectMaestro = async (maestroId) => {
    try {
      const res = await axios.post(`${API}/athlete/select-maestro/${maestroId}`);
      if (res.data.status === 'conflict') {
        setConflictData(res.data);
        setPendingMaestroId(maestroId);
      } else {
        toast.success(res.data.message);
        const m = maestros.find(m => m.id === maestroId);
        setCurrentMaestro(m);
        setSearch('');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const confirmTransfer = async () => {
    try {
      const res = await axios.post(`${API}/athlete/confirm-transfer-maestro/${pendingMaestroId}`);
      toast.success(res.data.message);
      const m = maestros.find(m => m.id === pendingMaestroId);
      setCurrentMaestro(m);
      setConflictData(null);
      setPendingMaestroId(null);
      setSearch('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const filtered = search.length >= 2
    ? maestros.filter(m => `${m.name} ${m.surname} ${m.school_name || ''}`.toLowerCase().includes(search.toLowerCase())).slice(0, 8)
    : [];

  return (
    <div>
      {currentMaestro && (
        <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200" data-testid="current-maestro-info">
          <h4 className="font-semibold text-purple-900 mb-2">Maestro Attuale</h4>
          <p className="font-medium">{currentMaestro.name} {currentMaestro.surname}</p>
          {currentMaestro.school_name && <p className="text-sm text-purple-700">Scuola: {currentMaestro.school_name}</p>}
          {currentMaestro.school_address && <p className="text-sm text-gray-600">{currentMaestro.school_address}</p>}
          {currentMaestro.school_phone && <p className="text-sm text-gray-600">Tel: {currentMaestro.school_phone}</p>}
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca maestro per nome o scuola..." className="pl-9" data-testid="maestro-search-input" />
      </div>

      {filtered.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="maestro-search-results">
          {filtered.map(m => (
            <div key={m.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
              <div>
                <p className="font-medium">{m.name} {m.surname}</p>
                {m.school_name && <p className="text-sm text-gray-600">Scuola: {m.school_name}</p>}
              </div>
              <Button size="sm" onClick={() => selectMaestro(m.id)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`select-maestro-${m.id}`}>
                Seleziona
              </Button>
            </div>
          ))}
        </div>
      )}

      {conflictData && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="athlete-transfer-dialog">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold mb-3">Trasferimento Scuola</h3>
            <p className="text-sm text-gray-700 mb-4">{conflictData.message}</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setConflictData(null); setPendingMaestroId(null); }}>Annulla</Button>
              <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={confirmTransfer}>Conferma Trasferimento</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AtletaDashboard = () => {
  const {
    user, activeRole,
    setShowProfileModal, isAthleteProfileComplete,
    activeAthleteTab, setActiveAthleteTab,
    mobileMenuOpen, setMobileMenuOpen,
    myEvents, myEnrollments, matchAnalyses, certificates, detailedMatchAnalyses,
    fetchStudyMaterials, setSelectedEventForMaterials,
    formatDate, formatDateRange
  } = useDashboard();

  return (
    <>
      {/* Profile Button */}
      <div className="mb-4 flex justify-end">
        <Button
          onClick={() => setShowProfileModal(true)}
          className={`
            ${!isAthleteProfileComplete 
              ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse' 
              : 'bg-white text-black border-2 border-gray-300 hover:bg-gray-50'
            }
            font-semibold px-6 py-2 transition-all duration-300
          `}
          data-testid="athlete-profile-button"
        >
          <User className="w-4 h-4 mr-2" />
          PROFILO
          {!isAthleteProfileComplete && (
            <span className="ml-2 inline-flex items-center justify-center w-2 h-2 bg-white rounded-full"></span>
          )}
        </Button>
      </div>

      <Tabs value={activeAthleteTab} onValueChange={setActiveAthleteTab} className="space-y-6" data-testid="atleta-dashboard">
        <TabsList className="hidden md:grid w-full grid-cols-8">
          <TabsTrigger value="events" data-testid="events-tab">I Miei Eventi</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="enrollments-tab">Le Mie Iscrizioni</TabsTrigger>
          <TabsTrigger value="maestro" data-testid="maestro-tab">Il Mio Maestro</TabsTrigger>
          <TabsTrigger value="competition" data-testid="athlete-competition-tab">Giudizi Gara</TabsTrigger>
          <TabsTrigger value="analysis" data-testid="analysis-tab">Match Analysis</TabsTrigger>
          <TabsTrigger value="detailed" data-testid="detailed-tab">Analysis Dettagliata</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="certificates-tab">Certificati</TabsTrigger>
          <TabsTrigger value="booklet" data-testid="booklet-tab">Libretto Atleta</TabsTrigger>
        </TabsList>
        
        {/* Mobile Menu */}
        <div className="md:hidden relative">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg shadow-sm"
            data-testid="mobile-menu-button"
          >
            <div className="flex items-center space-x-2">
              <Menu className="w-5 h-5 text-gray-600" />
              <span className="text-gray-600">Scegli sezione</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-red-600">
                {activeAthleteTab === 'events' && 'I Miei Eventi'}
                {activeAthleteTab === 'enrollments' && 'Le Mie Iscrizioni'}
                {activeAthleteTab === 'competition' && 'Giudizi Gara'}
                {activeAthleteTab === 'analysis' && 'Match Analysis'}
                {activeAthleteTab === 'detailed' && 'Analysis Dettagliata'}
                {activeAthleteTab === 'certificates' && 'Certificati'}
                {activeAthleteTab === 'booklet' && 'Libretto Atleta'}
              </span>
              <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
            </div>
          </button>
          
          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50">
              {[
                { value: 'events', label: 'I Miei Eventi' },
                { value: 'enrollments', label: 'Le Mie Iscrizioni' },
                { value: 'competition', label: 'Giudizi Gara' },
                { value: 'analysis', label: 'Match Analysis' },
                { value: 'detailed', label: 'Analysis Dettagliata' },
                { value: 'certificates', label: 'Certificati' },
                { value: 'booklet', label: 'Libretto Atleta' }
              ].map((item) => (
                <button
                  key={item.value}
                  onClick={() => { setActiveAthleteTab(item.value); setMobileMenuOpen(false); }}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 ${
                    activeAthleteTab === item.value ? 'bg-red-50 text-red-600 font-medium' : 'text-gray-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      
        <TabsContent value="competition" data-testid="athlete-competition-content">
          <CompetitionAnalysis user={user} activeRole={activeRole} />
        </TabsContent>
        
        <TabsContent value="events" data-testid="events-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>I Miei Eventi</span>
              </CardTitle>
              <CardDescription>Eventi ai quali sei iscritto</CardDescription>
            </CardHeader>
            <CardContent>
              {myEvents.length === 0 ? (
                <p className="text-gray-500 text-center py-8" data-testid="no-events">Non sei ancora iscritto a nessun evento</p>
              ) : (
                <div className="space-y-4" data-testid="my-events-list">
                  {myEvents.map((event) => (
                    <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`my-event-${event.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg" data-testid={`event-title-${event.id}`}>{event.title}</h3>
                          <p className="text-gray-600 mb-2" data-testid={`event-description-${event.id}`}>{event.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span data-testid={`event-date-${event.id}`}>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                            <Badge variant="secondary" data-testid={`event-type-${event.id}`}>{event.type}</Badge>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          onClick={async () => { await fetchStudyMaterials(event.id); setSelectedEventForMaterials(event); }}
                          data-testid={`athlete-study-materials-${event.id}`}>
                          <BookOpen className="w-4 h-4 mr-1" /> Materiale Didattico
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="enrollments" data-testid="enrollments-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="w-5 h-5" />
                <span>Le Mie Iscrizioni agli Eventi</span>
              </CardTitle>
              <CardDescription>Visualizza lo stato delle tue iscrizioni agli eventi</CardDescription>
            </CardHeader>
            <CardContent>
              {myEnrollments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Non hai ancora nessuna iscrizione</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {myEnrollments.map((enrollment) => (
                    <div key={enrollment.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{enrollment.event_title}</h3>
                          <p className="text-sm text-gray-600">
                            Iscritto il: {new Date(enrollment.enrolled_at).toLocaleDateString('it-IT', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                          {enrollment.approved_at && (
                            <p className="text-xs text-gray-500">
                              {enrollment.status === 'approved' ? 'Approvato' : 'Elaborato'} il: {new Date(enrollment.approved_at).toLocaleDateString('it-IT', {
                                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            enrollment.status === 'approved' && enrollment.paid !== false
                              ? 'bg-green-100 text-green-800' 
                              : enrollment.status === 'approved' && enrollment.paid === false
                              ? 'bg-orange-100 text-orange-800'
                              : enrollment.status === 'pending_payment'
                              ? 'bg-orange-100 text-orange-800'
                              : enrollment.status === 'pending' 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`} data-testid={`enrollment-status-${enrollment.id}`}>
                            {enrollment.status === 'approved' && enrollment.paid !== false && 'ISCRITTO - PAGATO'}
                            {enrollment.status === 'approved' && enrollment.paid === false && 'ISCRITTO - Pagamento da effettuare'}
                            {enrollment.status === 'pending_payment' && 'ISCRITTO - Pagamento da effettuare'}
                            {enrollment.status === 'pending' && 'In Attesa di Approvazione'}
                            {enrollment.status === 'rejected' && 'Rifiutata'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="analysis" data-testid="analysis-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BookOpen className="w-5 h-5" />
                <span>Le Mie Match Analysis</span>
              </CardTitle>
              <CardDescription>Feedback ricevuti dai tuoi insegnanti</CardDescription>
            </CardHeader>
            <CardContent>
              {matchAnalyses.length === 0 ? (
                <p className="text-gray-500 text-center py-8" data-testid="no-analysis">Non hai ancora ricevuto nessuna Match Analysis</p>
              ) : (
                <div className="space-y-4" data-testid="analysis-list">
                  {matchAnalyses.map((analysis) => (
                    <div key={analysis.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`analysis-${analysis.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold" data-testid={`analysis-event-${analysis.id}`}>{analysis.event_title}</h3>
                        <span className="text-sm text-gray-500" data-testid={`analysis-date-${analysis.id}`}>{formatDate(analysis.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2" data-testid={`analysis-teacher-${analysis.id}`}>Insegnante: {analysis.teacher_name}</p>
                      {analysis.result_score != null && (
                        <div className="flex items-center gap-3 mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200" data-testid={`analysis-score-${analysis.id}`}>
                          <span className="text-sm font-semibold text-purple-700">Punteggio Totale:</span>
                          <span className="text-2xl font-bold text-purple-800">{analysis.result_score}</span>
                        </div>
                      )}
                      <div className="bg-gray-50 rounded p-3 mb-2" data-testid={`analysis-content-${analysis.id}`}>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Descrizione Risultato</p>
                        <p className="text-gray-700 whitespace-pre-wrap">{analysis.content}</p>
                      </div>
                      {analysis.judgments_advice && (
                        <div className="bg-blue-50 rounded p-3 border border-blue-100" data-testid={`analysis-advice-${analysis.id}`}>
                          <p className="text-xs font-semibold text-blue-600 mb-1">Giudizi e Consigli</p>
                          <p className="text-sm text-blue-800 whitespace-pre-wrap">{analysis.judgments_advice}</p>
                        </div>
                      )}
                      {analysis.has_certificate && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const resp = await axios.get(
                                  `${API}/match-analysis/${analysis.id}/certificate`,
                                  { responseType: 'blob' }
                                );
                                const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                const a = document.createElement('a');
                                a.href = url;
                                a.target = '_blank';
                                a.rel = 'noopener noreferrer';
                                a.download = analysis.certificate_pdf_filename || `certificato_${analysis.id}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                setTimeout(() => window.URL.revokeObjectURL(url), 5000);
                              } catch (err) {
                                console.error('Errore download certificato', err);
                              }
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-sm font-medium"
                            data-testid={`analysis-certificate-${analysis.id}`}
                          >
                            <span>📎</span>
                            <span>Scarica Certificato/Attestato{analysis.certificate_pdf_filename ? ` (${analysis.certificate_pdf_filename})` : ''}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="detailed" data-testid="detailed-analysis-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BookOpen className="w-5 h-5" />
                <span>Le Mie Analysis Dettagliate</span>
              </CardTitle>
              <CardDescription>Analisi dettagliate con punteggi ricevute dai tuoi insegnanti</CardDescription>
            </CardHeader>
            <CardContent>
              {detailedMatchAnalyses.length === 0 ? (
                <p className="text-gray-500 text-center py-8" data-testid="no-detailed-analysis">Non hai ancora ricevuto nessuna analisi dettagliata</p>
              ) : (
                <div className="space-y-4" data-testid="detailed-analysis-list">
                  {detailedMatchAnalyses.map((analysis) => (
                    <div key={analysis.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`detailed-analysis-${analysis.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-semibold text-lg" data-testid={`detailed-analysis-event-${analysis.id}`}>{analysis.event_title}</h3>
                          <p className="text-sm text-gray-600 mb-1" data-testid={`detailed-analysis-teacher-${analysis.id}`}>Insegnante: {analysis.teacher_name}</p>
                          <span className="text-sm text-gray-500" data-testid={`detailed-analysis-date-${analysis.id}`}>{formatDate(analysis.created_at)}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600" data-testid={`detailed-analysis-total-${analysis.id}`}>{analysis.total_score}/10</div>
                          <div className="text-sm text-gray-500">Punteggio Totale</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                        {Object.entries(analysis.scores || {}).map(([key, value]) => (
                          <div key={key} className="text-center p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200" data-testid={`detailed-score-display-${key}-${analysis.id}`}>
                            <div className="text-xs text-blue-600 mb-2 font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                            <div className="text-lg font-bold text-blue-800">{value}/10</div>
                            <div className="flex justify-center mt-1"><StarRating value={value} readonly={true} /></div>
                          </div>
                        ))}
                      </div>
                      {analysis.notes && (
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-testid={`detailed-analysis-notes-${analysis.id}`}>
                          <h4 className="font-medium text-green-800 mb-2">Note dell'Insegnante:</h4>
                          <p className="text-green-700 whitespace-pre-wrap">{analysis.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        

        {/* My Maestro Tab */}
        <TabsContent value="maestro" data-testid="maestro-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Il Mio Maestro / Scuola
              </CardTitle>
              <CardDescription>Seleziona o visualizza il tuo maestro e la scuola di appartenenza</CardDescription>
            </CardHeader>
            <CardContent>
              <MaestroSelector user={user} />
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="certificates" data-testid="certificates-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Award className="w-5 h-5" />
                <span>I Miei Certificati</span>
              </CardTitle>
              <CardDescription>Certificati e attestati ottenuti</CardDescription>
            </CardHeader>
            <CardContent>
              {certificates.length === 0 ? (
                <p className="text-gray-500 text-center py-8" data-testid="no-certificates">Non hai ancora ricevuto nessun certificato</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2" data-testid="certificates-grid">
                  {certificates.map((cert) => (
                    <div key={cert.id} className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-testid={`certificate-${cert.id}`}>
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Award className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-blue-900" data-testid={`cert-title-${cert.id}`}>{cert.title}</h3>
                          {cert.description && <p className="text-blue-700 text-sm mt-1" data-testid={`cert-description-${cert.id}`}>{cert.description}</p>}
                          <p className="text-blue-600 text-xs mt-2" data-testid={`cert-date-${cert.id}`}>Rilasciato il {formatDate(cert.issued_at)}</p>
                          {cert.pdf_filename && cert.file_available !== false ? (
                            <Button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const response = await axios.get(`${API}/certificates/${cert.id}/download`, {
                                    responseType: 'blob', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `${cert.title.replace(/ /g, '_')}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  const errorMessage = error.response?.data?.detail || 'Errore nel download del certificato';
                                  toast.error(errorMessage + '. Contatta l\'admin per rigenerare il PDF.');
                                }
                              }}
                              size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid={`download-cert-${cert.id}`}>
                              <Download className="w-3 h-3 mr-1" /> Scarica PDF
                            </Button>
                          ) : cert.pdf_filename && cert.file_available === false ? (
                            <p className="text-xs text-orange-600 mt-2">PDF non disponibile - Contatta l'admin</p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="booklet" data-testid="booklet-content">
          <AthleteBooklet />
        </TabsContent>
      </Tabs>
    </>
  );
};
